import { NgModule } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { TransferState, makeStateKey } from '@angular/core';

import { Apollo, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloLink, InMemoryCache } from '@apollo/client/core';

import { ShellDomainProvider } from '@ecap3/core';

const STATE_KEY = makeStateKey<any>('apollo.state');

@NgModule({ exports: [ApolloModule], imports: [HttpClientModule], providers: [] })
export class GraphQLModule {
  private cache: InMemoryCache = new InMemoryCache({
    addTypename: false
  });

  constructor(apollo: Apollo,
    httpLink: HttpLink,
    private readonly transferState: TransferState) {
    const queryUri = ShellDomainProvider['api']['GQLQueryUri'];
    const mutationUri = ShellDomainProvider['api']['GQLMutationUri'];
    const { queryHttp, mutationHttp } = this.getHttpLink(httpLink, queryUri, mutationUri);

    const authMiddleware = this.middleware()

    const cacheRemoveConfig = {
      watchQuery: {
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
      },
      query: {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      }
    }

    // create Apollo Default
    this.createClient(apollo, authMiddleware, queryHttp, mutationHttp, cacheRemoveConfig);

    const isBrowser = this.transferState.hasKey<any>(STATE_KEY);

    if (isBrowser) {
      this.onBrowser();
    } else {
      this.onServer();
    }
  }

  private createClient(apollo: Apollo, authMiddleware: ApolloLink, queryHttp, mutationHttp, cacheRemoveConfig) {
    apollo.create({
      link: queryHttp,
      cache: this.cache,
      defaultOptions: cacheRemoveConfig,
      queryDeduplication: false
    });
    // create Apollo Mutations
    apollo.create({
      link: mutationHttp,
      cache: this.cache,
      defaultOptions: cacheRemoveConfig,
      queryDeduplication: false
    }, 'Mutations');

    apollo.create({
      link: queryHttp,
      cache: this.cache,
    }, 'QueryCache');

    // create Apollo Mutations
    apollo.create({
      link: mutationHttp,
      cache: this.cache,
    }, 'MutationsCache');
  }

  private middleware() {
    return new ApolloLink((operation, forward) => {
      const token = "eyJhbGciOiJodHRwOi8vd3d3LnczLm9yZy8yMDAxLzA0L3htbGRzaWctbW9yZSNyc2Etc2hhMjU2IiwidHlwIjoiSldUIn0.eyJ0ZW5hbnRfaWQiOiJGQzAwNjI3OC0yRDUxLTQ3MDMtOTUyQi1DMzEwMjY3NDYwMjYiLCJzdWIiOiI4MjQ2Nzk1OC00NzY4LTRlNjQtOTI4ZC1hZTkyYTg4ZWQ2NWUiLCJzaXRlX2lkIjoiNEFERDY4QzUtMkIzRC00NDc4LUFGQzYtMTE1MTBGOTU1QjkxIiwib3JpZ2luIjoic3dpc3NwYXJ0bmVyc3BvcnRhbC5mYWtlZG9tYWluLmNvbSIsInNlc3Npb25faWQiOiJlZTVlNTg3MTk4MTU0NzY0YTNiYThmMzk3OGRmMjY1NyIsInVzZXJfaWQiOiI4MjQ2Nzk1OC00NzY4LTRlNjQtOTI4ZC1hZTkyYTg4ZWQ2NWUiLCJkaXNwbGF5X25hbWUiOiJrYW1ydXp6YW1hbiB6YW1hbiIsInNpdGVfbmFtZSI6IkVjYXAgVGVhbSIsInVzZXJfbmFtZSI6Im1kLmthbXJ1enphbWFuQHNlbGlzZS5jaCIsImVtYWlsIjoibWQua2FtcnV6emFtYW5Ac2VsaXNlLmNoIiwicGhvbmVfbnVtYmVyIjoiKzg4MDE3MjI3NTM0OTciLCJsYW5ndWFnZSI6ImVuLVVTIiwidXNlcl9sb2dnZWRpbiI6IlRydWUiLCJuYW1lIjoiODI0Njc5NTgtNDc2OC00ZTY0LTkyOGQtYWU5MmE4OGVkNjVlIiwicm9sZSI6WyJhZG1pbiIsImFwcHVzZXIiLCJhbm9ueW1vdXMiXSwibmJmIjoxNTQ5MjAwMTc4LCJleHAiOjE1OTEyMDAxNzgsImlzcyI6IkNOPUVudGVycHJpc2UgQ2xvdWQgQXBwbGljYXRpb24gUGxhdGZvcm0iLCJhdWQiOiIqIn0.YwBsw1tRl2FwZyw-0r25g_Jbj7XvZ0FSqvbIRKnDJOTr5fF55n4mltDP0bm7c1SnSYjOsklgsoQ71NLHD1Mq3n4geJb_Vdf--elAgUZYJxQOxQgzNiQWf3DEOPiv_liUN9kcl_bRGoqtiWqZ9Ft4ZHEmTLcOMQim_tISXSc9F3NzXp2HL5nq6HFySpl21EOKoLpV2_KHRKhfVD9ePmWi5nfK9WX1WLJJKyWHYfJHHJoa_GRqEkLsunqmq7P4yADK_SEbtbKGVbhH7JixUjUf0gZjAvODN8SP1jmAPRNW5RWgxvG_JnxT0aF_BLbitCNQw94qgWtSdjg0qrgafegKug";
      operation.setContext(({ headers }) => ({
        headers: {
          Authorization: `Bearer ${token}`
        }
      }));
      return forward(operation);
    });
  }

  private getHttpLink(httpLink: HttpLink, queryUri: any, mutationUri: any) {
    const queryHttp = httpLink.create({
      uri: queryUri,
      withCredentials: true,
      method: 'POST'
    });
    const mutationHttp = httpLink.create({
      uri: mutationUri,
      withCredentials: true,
      method: 'POST'
    });
    return { queryHttp, mutationHttp };
  }

  onServer() {
    this.transferState.onSerialize(STATE_KEY, () => {
      return this.cache.extract();
    });
  }

  onBrowser() {
    const state = this.transferState.get<any>(STATE_KEY, null);
    this.cache.restore(state);
  }

}
